// Single source of truth for the dual-platform model.
//
// REMUS serves two independent backends — PROD and TEST — selected by the
// leading URL segment (`/PROD/...`, `/TEST/...`). Every API call, query key and
// live subscription is scoped to one platform. This module owns the platform
// concept: the type, the list, the default, validation, and the *active*
// platform derived from the browser URL.
//
// React components read the active platform reactively via `usePlatform()` (the
// route param). Non-React callers such as Axios api modules and STOMP managers
// read it via `getActivePlatform()`. Both reflect the same browser URL, which
// React Router updates synchronously on navigation, and both share the one
// validator here, so the two readers cannot diverge.

export type RemusPlatform = 'PROD' | 'TEST';

export const PLATFORMS: RemusPlatform[] = ['PROD', 'TEST'];

export const DEFAULT_PLATFORM: RemusPlatform = 'PROD';

export function isRemusPlatform(value: unknown): value is RemusPlatform {
  return value === 'PROD' || value === 'TEST';
}

// Parse the platform segment from a pathname such as "/TEST/trends". Falls back
// to DEFAULT_PLATFORM for the root path, an unknown segment, or a missing URL.
export function platformFromPathname(pathname: string | null | undefined): RemusPlatform {
  const segment = pathname ? pathname.split('/')[1] : undefined;
  return isRemusPlatform(segment) ? segment : DEFAULT_PLATFORM;
}

// Platforms this build exposes in the UI. The app build (Electron) sets
// VITE_APP_PLATFORMS in frontend/.env.app (e.g. "PROD,TEST") to control which
// platforms the header switcher offers; it must match the APP_PLATFORMS the
// Electron backend proxy serves (see electron/main.js). The web build leaves it
// unset and gets the full PLATFORMS list. A single-entry list hides the switcher.
export function getAppPlatforms(): RemusPlatform[] {
  const raw = import.meta.env.VITE_APP_PLATFORMS as string | undefined;
  if (!raw) {
    return PLATFORMS;
  }
  const parsed = raw
    .split(',')
    .map(segment => segment.trim().toUpperCase())
    .filter(isRemusPlatform);
  return parsed.length ? parsed : PLATFORMS;
}

// The platform the app is currently scoped to, derived from the browser URL.
// Safe to call before React mounts, so the very first requests already target
// the right backend.
export function getActivePlatform(): RemusPlatform {
  const pathname = typeof window !== 'undefined' ? window.location?.pathname : undefined;
  return platformFromPathname(pathname);
}

// Backwards-compatible default export: the list of platforms, imported as
// `platforms` by App.jsx and others.
const platforms = PLATFORMS;
export default platforms;
